import type { ICurrencyExchange } from "@/modules/transaction/common/types";
import { Amount } from "@/shared/components";
import { Checkbox } from "@ab.uitools/ui-library/components/Checkbox";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { Copy } from "@ab.uitools/ui-library/components/Copy";
import { Divider } from "@ab.uitools/ui-library/components/Divider";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { Tooltip } from "@ab.uitools/ui-library/components/Tooltip";
import { Positions } from "@ab.uitools/ui-library/components/Tooltip/types";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  transfer: ICurrencyExchange;
  scrollbarContainerRef?: RefObject<HTMLDivElement | null>;
}

export const CurrencyExchangeDetailRows = ({
  transfer,
  scrollbarContainerRef
}: Props) => {
  const { t } = useTranslation();

  return (
    <div>
      <div>
        <CollapseGroup
          className="detail-collapse"
          items={[
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.currencyExchange.fromAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={`${transfer.debitAccount?.number}`}
                        />
                        <Text size="medium" weight="bold">{transfer.debitAccount?.number}</Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.currencyExchange.toAccount")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={`${transfer.creditAccount?.number}`}
                        />
                        <Text size="medium" weight="bold">{transfer.creditAccount?.number}</Text>
                      </div>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.currencyExchange.iHave")}
                      </Text>
                      <Amount currency={transfer.currency} amount={transfer.amount as number}/>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.currencyExchange.iWant")}
                      </Text>
                      <Amount
                        currency={transfer.creditCurrency}
                        amount={transfer.creditAmount as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.currencyExchange.preferentialRate")}
                      </Text>
                      <Amount amount={transfer.preferentialRate as number}
                      />
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="flexbox justify-content--between pt-20 pb-4 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold"
                      >
                        {t("general.purpose")}
                      </Text>
                      <Tooltip
                        position={Positions.TOP_CENTER}
                        id="currency-exchange-purpose-tooltip"
                        text={transfer.purpose}
                      />
                      <Text
                        id="currency-exchange-purpose-tooltip"
                        className="truncate cursor-pointer pl-16"
                        size="medium"
                        weight="bold"
                      >
                        {transfer.purpose}
                      </Text>
                    </div>
                    <Divider
                      color="light"
                      type="primary"
                      className="mt-16"
                      isHorizontal
                    />
                    <div className="pt-20 pb-20 detail-row">
                      <Checkbox
                        disabled
                        selectedValue={transfer.isRateChangeAcceptable}
                        label={t("transfer.currencyExchange.agreeWithRateChanging")}
                      />
                    </div>
                  </div>
                </>
              ),
              value: "transfer_details",
              isOpen: true,
              title: t("transfer.transferDetails")
            },
            {
              content: (
                <>
                  <div>
                    <div className="flexbox justify-content--between pt-20 pb-20 detail-row">
                      <Text
                        className="text-nowrap"
                        type="secondary"
                        size="medium"
                        weight="semibold">
                        {t("transfer.transactionNumber")}
                      </Text>
                      <div className="flexbox align-items--center">
                        <Copy
                          textAfterCopy={t("general.copied")}
                          size="medium"
                          parentRef={scrollbarContainerRef as RefObject<HTMLDivElement>}
                          text={transfer.transactionNumber as string}
                        />
                        <Text size="medium" weight="bold">
                          {transfer.transactionNumber}
                        </Text>
                      </div>
                    </div>
                  </div>
                </>
              ),
              value: "other_details",
              isOpen: true,
              title: t("general.otherDetails")
            },
          ]}
        />
      </div>
    </div>
  );
};
